/**
 * Meal plan list provider.
 */
import {
  createContext,
  useContext as useReactContext,
  useMemo,
  type PropsWithChildren,
} from "react";
import { useQuery, useZero } from "@corpuscomplete/zero-data-transformer/react";
import type { Zero, PromiseWithServerResult } from "@rocicorp/zero";
import type { MealPlan } from "@corpuscomplete/core/nutrition/meal-plan";
import { mutators, queries } from "@corpuscomplete/core/zero";

export namespace MealPlanList {
  export type Context = {
    userId: string;
  };

  const DataContext = createContext<Context | null>(null);

  export type DataProviderProps = PropsWithChildren<{
    userId: string;
  }>;

  export function DataProvider({ children, userId }: DataProviderProps) {
    return <DataContext value={{ userId }}>{children}</DataContext>;
  }

  export function useContext(): Context {
    const context = useReactContext(DataContext);
    if (!context) {
      throw new Error("MealPlanList.useContext must be used within MealPlanList.DataProvider");
    }
    return context;
  }

  export type Preview = Omit<
    Pick<MealPlan.Info, "id" | "status" | "dateStart" | "dateEnd" | "timeCreated">,
    "timeCreated"
  > & {
    timeCreated: Temporal.Instant;
  };

  export function useMealPlans(): Preview[] {
    const { userId } = useContext();
    const [mealPlans] = useQuery(queries.mealPlan.forUser({ userId }));
    return mealPlans;
  }

  export function useLoadingState(): { isLoading: boolean } {
    const { userId } = useContext();
    const [, details] = useQuery(queries.mealPlan.forUser({ userId }));
    return {
      isLoading: details.type === "unknown",
    };
  }

  export type Mutations = {
    deleteMealPlan: (id: string) => PromiseWithServerResult;
  };

  export function useMutations(): Mutations {
    const z = useZero();

    return useMemo(
      () => ({
        deleteMealPlan: (id) => z.mutate(mutators.mealPlan.delete({ id })),
      }),
      [z],
    );
  }

  export function preload(zero: Zero, userId: string): void {
    void zero.run(queries.mealPlan.forUser({ userId }));
  }
}
